/**
 * @file Renders the submit button that runs the investment transfer and updates the moved investments' notes.
 */
import { useMemo, useState } from "react";
import keyBy from "lodash.keyby";
import { Button } from "antd";
import { useOutletContext } from "react-router-dom";

import { getConfig } from "@components/Config";
import { getUsDateFromIsoDate } from "@components/Date";
import { transferTransactionsOperation } from "@api/services/transactions";
import { transferInvestorFilesOperation } from "@api/services/backstage";
import {
  updateInvestmentOperation,
  transferInvestmentsOperation,
} from "@api/services/investments";
import { useAppContext } from "@components/AppContext";
import { type AppContext } from "@components/AppContext/types";

import getPayloads from "./helpers/getPayloads";
import useTransferItems from "./helpers/useTransferItems";
import useTargetBatches from "./helpers/useTargetBatches";
import type {
  Batch,
  TableRef,
  Investment,
  Transaction,
  InvestorFile,
  CustomTarget,
  CustomAmounts,
  OutletContext,
  CustomAmountsMap,
} from "./types";

const LABEL_SUBMIT = "Start Transfer";

const RESTRICT_TRANSFER_INVESTMENTS_SUBMIT_TO_EMAILS: string[] =
  getConfig("RESTRICT_TRANSFER_INVESTMENTS_SUBMIT_TO_EMAILS");

type Props = {
  table: TableRef;
  sourceFiles: InvestorFile[];
  customAmounts: CustomAmounts[];
  customTargets: CustomTarget[];
  sourceBatches: Batch[];
  targetBatches: Batch[];
  sourceProjectId: string;
  targetProjectId?: string;
  onAfterComplete: () => Promise<void>;
  setTargetBatches: (batches: Batch[]) => void;
  sourceInvestments: Investment[];
  targetInvestments: Investment[];
  sourceTransactions: Transaction[];
};

/**
 * Renders the submit button that transfers the selected investments, files and transactions to the target project.
 */
const InvestmentsTransferFooter = ({
  table,
  sourceFiles,
  customAmounts,
  customTargets,
  sourceBatches,
  targetBatches,
  sourceProjectId,
  targetProjectId = undefined,
  onAfterComplete,
  setTargetBatches,
  sourceInvestments,
  targetInvestments,
  sourceTransactions,
}: Props) => {
  const [ isLoading, setIsLoading ] = useState(false);

  const { getProjectName } = useOutletContext<OutletContext>();
  const { request, identity } = useAppContext() as AppContext;

  const isTransferInvestmentsSubmitAvailable =
    RESTRICT_TRANSFER_INVESTMENTS_SUBMIT_TO_EMAILS.length === 0 ||
    RESTRICT_TRANSFER_INVESTMENTS_SUBMIT_TO_EMAILS.includes(identity?.email);

  const customAmountsMap = useMemo((): CustomAmountsMap =>
    keyBy(customAmounts, "investmentId")
  , [ customAmounts ]);

  const {
    investmentsToMove,
    investmentsToOverride,
    filesToMove,
    filesToOverride,
    transactionsToMove,
    transactionsToOverride,
  } = useTransferItems({
    table,
    targetProjectId,
    targetInvestments,
    sourceProjectId,
    sourceFiles,
    sourceInvestments,
    sourceTransactions,
    customTargets,
  });

  const {
    ensureTargetBatches,
  } = useTargetBatches({
    targetProjectId,
    filesToMove,
    filesToOverride,
    sourceBatches,
    targetBatches,
    setTargetBatches,
  });

  /**
   * Runs the transfer requests for the selected items and appends a transfer note to each moved investment.
   */
  const onClick = async () => {
    setIsLoading(true);

    const { getTargetBatchId } = await ensureTargetBatches();

    const {
      transferInvestmentsPayload,
      transferTransactionsPayload,
      transferInvestorFilesPayload,
    } = getPayloads({
      sourceProjectId,
      targetProjectId,
      sourceInvestments,
      filesToMove,
      filesToOverride,
      transactionsToMove,
      transactionsToOverride,
      investmentsToMove,
      investmentsToOverride,
      getProjectName,
      customAmountsMap,
      getTargetBatchId,
    });

    const payloads = {
      TransferInvestments: transferInvestmentsPayload,
      TransferTransactions: transferTransactionsPayload,
      TransferInvestorFiles: transferInvestorFilesPayload,
    };

    // eslint-disable-next-line no-console
    console.info(JSON.stringify(payloads, null, 2));

    await request(transferInvestorFilesOperation, { mutation: transferInvestorFilesPayload });
    await request(transferTransactionsOperation, { mutation: transferTransactionsPayload });
    await request(transferInvestmentsOperation, { mutation: transferInvestmentsPayload });

    const transferredInvestments = [
      ...transferInvestmentsPayload.move,
      ...transferInvestmentsPayload.override,
    ];

    for (const transferredInvestment of transferredInvestments) {
      const { investmentId } = transferredInvestment;

      const investment = [
        ...sourceInvestments,
        ...targetInvestments,
      ].find(({ id }) => id === investmentId);

      const { note } = investment;

      const usDate = getUsDateFromIsoDate(new Date());
      const sourceProjectName = getProjectName(sourceProjectId);
      const targetProjectName = getProjectName(targetProjectId);

      const updatedNote = [
        note,
        `Transferred from ${sourceProjectName} to ${targetProjectName} on ${usDate}`,
      ].filter((value) => !!value).join("\n");

      await request(updateInvestmentOperation, { id: investmentId, mutation: { note: updatedNote } });
    }

    await onAfterComplete();
  };

  const isDisabled =
    !targetProjectId ||
    !isTransferInvestmentsSubmitAvailable;

  return (
    <Button
      type="primary"
      onClick={onClick}
      loading={isLoading}
      disabled={isDisabled}
    >
      {LABEL_SUBMIT}
    </Button>
  );
};

export default InvestmentsTransferFooter;
